/**
 * @file ProductReviewPanels.tsx
 * @description Раскрывающиеся панели под отзывом (phase19.10): своя причина
 * отклонения, публичный ответ магазина, история модерации.
 *
 * Компоненты без собственного состояния — значения и обработчики приходят из
 * ProductReviewActions. Панели правят только ответ магазина и причину; текст
 * самого отзыва здесь не редактируется (R3 docs/business/reviews.md).
 */

import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import type { ReviewHistoryEntry } from "../../../lib/api-product-reviews";

/** Оформление кнопок панели — совпадает с action-строкой (mobile 44px). */
interface ButtonStyleProps {
  btnSize: "default" | "sm";
  btnClass: string;
}

export function RejectReasonPanel({
  value,
  onChange,
  onSubmit,
  onCancel,
  busy,
  btnSize,
  btnClass,
}: ButtonStyleProps & {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  busy: boolean;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-lg bg-surface-low p-3">
      <Textarea
        rows={2}
        value={value}
        placeholder="Причина отклонения — её увидит автор отзыва"
        onChange={(e) => onChange(e.target.value)}
      />
      <div className="flex gap-2">
        <Button
          size={btnSize}
          className={btnClass}
          disabled={busy || value.trim().length === 0}
          onClick={onSubmit}
        >
          Отклонить
        </Button>
        <Button variant="ghost" size={btnSize} className={btnClass} onClick={onCancel}>
          Отмена
        </Button>
      </div>
    </div>
  );
}

export function ShopReplyPanel({
  value,
  onChange,
  onSave,
  onDelete,
  canDelete,
  busy,
  btnSize,
  btnClass,
}: ButtonStyleProps & {
  value: string;
  onChange: (value: string) => void;
  onSave: () => void;
  onDelete: () => void;
  canDelete: boolean;
  busy: boolean;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-lg bg-surface-low p-3">
      <Textarea
        rows={3}
        value={value}
        placeholder="Публичный ответ магазина — виден на карточке товара"
        onChange={(e) => onChange(e.target.value)}
      />
      <div className="flex gap-2">
        <Button
          size={btnSize}
          className={btnClass}
          disabled={busy || value.trim().length === 0}
          onClick={onSave}
        >
          Сохранить ответ
        </Button>
        {canDelete ? (
          <Button
            variant="ghost"
            size={btnSize}
            className={btnClass}
            disabled={busy}
            onClick={onDelete}
          >
            Удалить ответ
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function ModerationHistoryPanel({
  entries,
}: {
  /** null — ещё грузится. */
  entries: ReviewHistoryEntry[] | null;
}) {
  return (
    <ul className="flex flex-col gap-1 rounded-lg bg-surface-low p-3 text-xs text-on-surface-variant">
      {entries === null ? (
        <li>Загрузка…</li>
      ) : entries.length === 0 ? (
        <li>Действий пока не было.</li>
      ) : (
        entries.map((h) => (
          <li key={h.id} className="flex flex-wrap gap-x-2">
            <span className="tabular-nums">
              {new Date(h.createdAt).toLocaleString("ru-RU")}
            </span>
            <span className="text-on-surface">{h.action}</span>
            {h.actor ? <span>· {h.actor}</span> : null}
            {h.comment ? <span>· {h.comment}</span> : null}
          </li>
        ))
      )}
    </ul>
  );
}
